// src/components/SiteFooter.jsx
import React from "react";
import { Link } from "react-router-dom";
import {
  Instagram, Twitter, Youtube, Facebook, Linkedin, Mail, Phone, MapPin,
} from "lucide-react";
import { FaTiktok, FaPinterestP } from 'react-icons/fa';

// --- Data for Links (Improved Maintainability) ---

const socialLinks = [
  { href: "https://www.facebook.com/aihairstylestudio/", name: "Facebook", icon: Facebook },
  { href: "https://www.instagram.com/aihairstylestudio", name: "Instagram", icon: Instagram },
  { href: "https://www.tiktok.com/@aihairstylestudio", name: "TikTok", icon: FaTiktok },
  { href: "https://www.youtube.com/@AIHairstyleStudio", name: "YouTube", icon: Youtube },
  { href: "https://uk.pinterest.com/aihairstylestudio/", name: "Pinterest", icon: FaPinterestP },
];

const footerNav = {
  solutions: [
    { name: "AI Hairstyle", to: "/app" },
    { name: "Random Hairstyle Generator", to: "/random-hairstyle-generator" },
    { name: "Face Shape Analyzer", to: "/which-hairstyle-suits-me-male-female" },
    { name: "Hair Ingredient Checker", to: "/hair-ingredient-checker" },
    { name: "My Creations", to: "/creations" },
    { name: "AI Hair Gallery", to: "/styles" },
  ],
  discover: [
    { name: "Find Professionals", to: "/professionals" },
    { name: "List Your Business", to: "/partners" },
    { name: "Baldness Calculator", to: "/norwood-hamilton-scale-will-i-go-bald-calculator" },
    { name: "Pricing", to: "/pricing" },
    { name: "Shop The Look", to: "/shop" },
    { name: "Blog", to: "/blog" },
  ],
  company: [
    { name: "About", to: "/about" },
    { name: "Contact", to: "/contact" },
    { name: "Careers", to: "/careers" },
    { name: "Admin", to: "/admin" },
  ],
};

const contactInfo = {
  email: "online@aihairstylestudio.com",
  phone: "+447521967542",
  phoneDisplay: "(+44) 07521967542",
  address: "131 Raglan Rd, Birmingham, B66 3SR, United Kingdom",
};

// --- Main Footer Component ---

export default function SiteFooter() {
  return (
    <footer className="bg-[#0b0b12] border-t border-white/10" role="contentinfo">
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-6">
          {/* Brand & Socials */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 text-2xl font-extrabold tracking-tight group">
              <img src="/logo.png" alt="AI Hairstyle Studio logo" className="transition-transform group-hover:scale-110 h-9 w-9 shrink-0" />
              <span className="align-middle">AI Hairstyle <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-500 to-cyan-400">Studio</span></span>
            </Link>
            <p className="mt-2 text-sm text-gray-400">Transform your look with AI-powered hairstyle visualization</p>
            <div className="mt-5 flex items-center gap-3" aria-label="Social links">
              {socialLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full border border-white/10 p-2 text-white/80 hover:bg-white/10"
                  >
                    <span className="sr-only">{link.name}</span>
                    <Icon size={18} aria-hidden="true" />
                  </a>
                );
              })}
            </div>

            {/* App Store Links */}
            <div className="mt-6 flex flex-col gap-2.5">
              <a
                href="https://play.google.com/store/apps/details?id=com.devzam.makebald&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-fit group"
              >
                <div className="rounded-lg bg-white/5 border border-white/10 p-1.5 hover:bg-white/10 hover:border-white/20 transition-all duration-200 hover:shadow-lg hover:shadow-fuchsia-500/10">
                  <img
                    src="/images/badges/google-play-badge.svg"
                    alt="Get it on Google Play"
                    className="h-9 w-auto"
                    loading="lazy"
                  />
                </div>
              </a>
              <a
                href="https://apps.apple.com/us/app/bald-filter-ai-hairstyle/id6742877127"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-fit group"
              >
                <div className="rounded-lg bg-white/5 border border-white/10 p-1.5 hover:bg-white/10 hover:border-white/20 transition-all duration-200 hover:shadow-lg hover:shadow-fuchsia-500/10">
                  <img
                    src="/images/badges/app-store-badge.svg"
                    alt="Download on the App Store"
                    className="h-9 w-auto"
                    loading="lazy"
                  />
                </div>
              </a>
            </div>
          </div>

          {/* Footer Navigation */}
          <nav className="lg:col-span-4" aria-labelledby="footer-nav-heading">
            <h2 id="footer-nav-heading" className="sr-only">Footer navigation</h2>
            <div className="grid grid-cols-2 gap-8 md:grid-cols-5">
              <div>
                <h3 className="text-sm font-semibold tracking-wide text-white">Solutions</h3>
                <ul className="mt-4 space-y-3">
                  {footerNav.solutions.map((link) => (
                    <li key={link.name}>
                      <Link to={link.to} className="text-sm text-gray-400 hover:text-white transition-colors">{link.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold tracking-wide text-white">Discover</h3>
                <ul className="mt-4 space-y-3">
                  {footerNav.discover.map((link) => (
                    <li key={link.name}>
                      <Link to={link.to} className="text-sm text-gray-400 hover:text-white transition-colors">{link.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold tracking-wide text-white">Company</h3>
                <ul className="mt-4 space-y-3">
                  {footerNav.company.map((link) => (
                    <li key={link.name}>
                      <Link to={link.to} className="text-sm text-gray-400 hover:text-white transition-colors">{link.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Upvote Us */}
              <div>
                <h3 className="text-sm font-semibold tracking-wide text-white">Upvote Us</h3>
                <ul className="mt-4 space-y-4">
                  <li>
                    <Link to="/ai-hairstyle-reviews" className="text-sm text-gray-400 hover:text-white transition-colors">
                      Feedback
                    </Link>
                  </li>
                  <li>
                    <a
                      href="https://www.trustpilot.com/review/aihairstylestudio.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block group"
                    >
                      <div className="rounded-lg bg-white/5 border border-white/10 p-2 hover:bg-white/10 hover:border-white/20 transition-all duration-200 hover:shadow-lg hover:shadow-fuchsia-500/10">
                        <img
                          src="/images/TrustPilot.png"
                          alt="Visit us on Trustpilot"
                          className="h-9 w-auto"
                          loading="lazy"
                        />
                      </div>
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.producthunt.com/posts/ai-hairstyle-3?embed=true&utm_source=badge-featured&utm_medium=badge&utm_souce=badge-ai-hairstyle-3"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block group"
                    >
                      <div className="rounded-lg bg-white/5 border border-white/10 p-1.5 hover:bg-white/10 hover:border-white/20 transition-all duration-200 hover:shadow-lg hover:shadow-fuchsia-500/10">
                        <img
                          src="/images/badges/product-hunt-badge.svg"
                          alt="Featured on Product Hunt"
                          className="h-9 w-auto"
                          loading="lazy"
                        />
                      </div>
                    </a>
                  </li>
                </ul>
              </div>

              {/* Contact Us */}
              <div>
                <h3 className="text-sm font-semibold tracking-wide text-white">Contact Us</h3>
                <ul className="mt-4 space-y-3 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <Mail size={16} className="mt-0.5 flex-shrink-0 text-cyan-400" />
                    <a href={`mailto:${contactInfo.email}`} className="text-cyan-400 hover:text-cyan-300 transition-colors break-all">
                      {contactInfo.email}
                    </a>
                  </li>
                  <li className="flex items-start gap-2">
                    <Phone size={16} className="mt-0.5 flex-shrink-0 text-cyan-400" />
                    <a href={`tel:${contactInfo.phone}`} className="text-cyan-400 hover:text-cyan-300 transition-colors">
                      {contactInfo.phoneDisplay}
                    </a>
                  </li>
                  <li className="flex items-start gap-2">
                    <MapPin size={16} className="mt-0.5 flex-shrink-0 text-gray-400" />
                    <span className="text-xs leading-relaxed text-gray-400">{contactInfo.address}</span>
                  </li>
                </ul>
              </div>
            </div>
          </nav>
        </div>

        <hr className="my-8 border-white/10" />

        <div className="flex flex-col items-start justify-between gap-4 md:flex-row">
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} AI Hairstyle Studio. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-6 text-sm">
            <Link to="/privacy" className="text-gray-400 hover:text-white">Privacy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-white">Terms</Link>
            <a href="/sitemap.xml" className="text-gray-400 hover:text-white">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}


